import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Home, ArrowLeft, User, Mail, Phone, MapPin, FileText, Check, DollarSign, Camera } from "lucide-react";
import { PhotoViewer } from "../components/wizard/PhotoViewer";
import { photoFileUrl } from "../services/uploadService";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Budget tier options
const BUDGET_TIERS = [
  { value: "Less than $1,000", label: "Less than $1,000" },
  { value: "$1,000–$5,000", label: "$1,000 – $5,000" },
  { value: "$5,000–$10,000", label: "$5,000 – $10,000" },
  { value: "$10,000+", label: "$10,000+" }
];

export default function RequestContact() {
  const navigate = useNavigate();
  const { user, token, login, register, googleLogin } = useAuth();
  
  const [wizardData, setWizardData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("register"); // register or login
  const [viewerIndex, setViewerIndex] = useState(null);
  
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: "",
    description: "",
    num_contractors: "3",
    budget_tier: ""  // Budget range for lead pricing
  });
  
  const [authData, setAuthData] = useState({
    email: "",
    password: "",
    name: ""
  });

  useEffect(() => {
    // Load wizard data from session
    const stored = sessionStorage.getItem("wizardData");
    if (!stored) {
      toast.error("Please start from the beginning");
      navigate("/request");
      return;
    }
    
    const data = JSON.parse(stored);
    setWizardData(data);
    
    // Auto-select budget tier based on estimated price
    if (data.estimated_range) {
      const avgEstimate = (data.estimated_range.low + data.estimated_range.high) / 2;
      let autoTier = "Less than $1,000";
      
      if (avgEstimate >= 10000) {
        autoTier = "$10,000+";
      } else if (avgEstimate >= 5000) {
        autoTier = "$5,000–$10,000";
      } else if (avgEstimate >= 1000) {
        autoTier = "$1,000–$5,000";
      }
      
      setFormData(prev => ({ ...prev, budget_tier: autoTier }));
    }
    
    // Pre-fill form if user is logged in
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || ""
      }));
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAuthChange = (e) => {
    setAuthData({ ...authData, [e.target.name]: e.target.value });
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    
    try {
      if (authMode === "login") {
        await login(authData.email, authData.password);
      } else {
        await register({
          email: authData.email,
          password: authData.password,
          name: authData.name || formData.name,
          role: "homeowner"
        });
      }
      setShowAuth(false);
      toast.success(authMode === "login" ? "Logged in!" : "Account created!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      // Show auth modal
      setAuthData({ ...authData, email: formData.email, name: formData.name });
      setShowAuth(true);
      return;
    }
    
    if (!formData.address || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Build description from wizard answers
      let description = formData.description || "";
      if (wizardData?.answers) {
        const answerSummary = Object.entries(wizardData.answers)
          .map(([key, value]) => {
            const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            const formattedValue = Array.isArray(value) ? value.join(', ') : value;
            return `${formattedKey}: ${formattedValue}`;
          })
          .join('\n');
        description = `${description}\n\n--- Project Details ---\n${answerSummary}`.trim();
      }
      
      // Find category ID from wizard data
      const requestData = {
        category_id: wizardData.category_id,
        title: `${wizardData.category_name} project in ${wizardData.zip_code}`,
        description: description,
        address: `${formData.address}, ${wizardData.zip_code}`,
        phone: formData.phone,
        num_contractors: parseInt(formData.num_contractors),
        preferred_date: null,
        budget_range: formData.budget_tier || null,
        job_budget_tier: formData.budget_tier || null,
        home_sqft: wizardData.home_sqft || null,
        answers: wizardData.answers || null,
        estimated_min: wizardData.estimated_range?.low || null,
        estimated_max: wizardData.estimated_range?.high || null,
        photo_session_id: wizardData.photo_session_id || null
      };
      
      await axios.post(`${API}/job-requests`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Clear wizard data
      sessionStorage.removeItem("wizardData");
      sessionStorage.removeItem("photoUploadSessionId");
      
      toast.success("Request submitted! Contractors will contact you soon.");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  if (!wizardData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  // Format answers for display
  const formatAnswerKey = (key) => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatAnswerValue = (value) => {
    if (Array.isArray(value)) {
      return value.map(v => v.replace(/_/g, ' ')).join(', ');
    }
    return String(value).replace(/_/g, ' ');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="request-contact">
      {/* Header */}
      <header className="border-b border-gray-100 sticky top-0 bg-white z-50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-[#1F2933] hidden sm:inline">Contractor Hub LLC</span>
          </Link>
          
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-[#1F2933]/70"
          >
            <ArrowLeft className="w-5 h-5 mr-2" /> Back
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        {/* Summary of selections */}
        <div className="mb-8 p-4 bg-[#0FA3B1]/5 rounded-2xl border border-[#0FA3B1]/20">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#0FA3B1] flex items-center justify-center">
              <Check className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1F2933]">{wizardData.category_name}</h3>
              <p className="text-sm text-[#1F2933]/60">Zip: {wizardData.zip_code}</p>
            </div>
          </div>
          
          {wizardData.answers && Object.keys(wizardData.answers).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#0FA3B1]/20">
              {Object.entries(wizardData.answers).slice(0, 4).map(([key, value]) => (
                <Badge key={key} variant="secondary" className="bg-white text-[#1F2933]/70 text-xs">
                  {formatAnswerValue(value)}
                </Badge>
              ))}
              {Object.keys(wizardData.answers).length > 4 && (
                <Badge variant="secondary" className="bg-white text-[#1F2933]/50 text-xs">
                  +{Object.keys(wizardData.answers).length - 4} more
                </Badge>
              )}
            </div>
          )}

          {/* Attached project photos */}
          {wizardData.photos?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#0FA3B1]/20" data-testid="review-photos-section">
              <p className="text-sm text-[#1F2933]/70 flex items-center gap-2 mb-2">
                <Camera className="w-4 h-4" />
                {wizardData.photos.length} project photo{wizardData.photos.length !== 1 ? 's' : ''} attached
              </p>
              <div className="flex flex-wrap gap-2">
                {wizardData.photos.map((p, i) => (
                  <button key={p.photo_id} type="button" onClick={() => setViewerIndex(i)} data-testid={`review-photo-${i}`}>
                    <img
                      src={photoFileUrl(p.photo_id, { sessionId: wizardData.photo_session_id })}
                      alt={p.caption || p.original_filename || "Project photo"}
                      className="w-14 h-14 rounded-lg object-cover border border-gray-200 hover:opacity-80 transition-opacity"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <h1 className="font-manrope font-bold text-2xl sm:text-3xl text-[#1F2933] text-center mb-2">
          Almost done! Tell us how to reach you.
        </h1>
        <p className="text-[#1F2933]/60 text-center mb-8">
          We'll connect you with local pros
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact Info */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <div className="relative mt-2">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F2933]/40" />
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className="pl-12 py-6"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F2933]/40" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="pl-12 py-6"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <div className="relative mt-2">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F2933]/40" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(512) 555-0123"
                  className="pl-12 py-6"
                  required
                  data-testid="phone-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="address">Street Address *</Label>
              <div className="relative mt-2">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F2933]/40" />
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="123 Main St"
                  className="pl-12 py-6"
                  required
                  data-testid="address-input"
                />
              </div>
              <p className="text-xs text-[#1F2933]/50 mt-1">Zip code: {wizardData.zip_code}</p>
            </div>

            <div>
              <Label htmlFor="description">Additional Details (Optional)</Label>
              <div className="relative mt-2">
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Any other details about your project..."
                  className="min-h-[100px]"
                  data-testid="description-input"
                />
              </div>
            </div>

            <div>
              <Label>How many contractors should contact you?</Label>
              <Select 
                value={formData.num_contractors} 
                onValueChange={(v) => setFormData({ ...formData, num_contractors: v })}
              >
                <SelectTrigger className="mt-2" data-testid="num-contractors-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 contractors</SelectItem>
                  <SelectItem value="3">3 contractors (Recommended)</SelectItem>
                  <SelectItem value="4">4 contractors</SelectItem>
                  <SelectItem value="5">5 contractors</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>What is your budget for this project?</Label>
              <div className="relative mt-2">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F2933]/40 z-10" />
                <Select 
                  value={formData.budget_tier} 
                  onValueChange={(v) => setFormData({ ...formData, budget_tier: v })}
                >
                  <SelectTrigger className="pl-12" data-testid="budget-tier-select">
                    <SelectValue placeholder="Select your budget range" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUDGET_TIERS.map(tier => (
                      <SelectItem key={tier.value} value={tier.value}>
                        {tier.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-[#1F2933]/50 mt-1">
                This helps us match you with the right contractors
              </p>
            </div>
          </div>

          <Button 
            type="submit"
            className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl btn-shine"
            disabled={submitting}
            data-testid="submit-btn"
          >
            {submitting ? (
              <div className="spinner w-5 h-5 border-2 border-white/20 border-t-white"></div>
            ) : (
              "Get Free Quotes"
            )}
          </Button>
          
          <p className="text-xs text-center text-[#1F2933]/50">
            By submitting, you agree to receive calls and texts from contractors.
          </p>
        </form>
      </main>

      {/* Photo viewer */}
      {wizardData.photos?.length > 0 && (
        <PhotoViewer
          photos={wizardData.photos.map((p) => ({
            src: photoFileUrl(p.photo_id, { sessionId: wizardData.photo_session_id }),
            caption: p.caption,
            name: p.original_filename
          }))}
          index={viewerIndex}
          onClose={() => setViewerIndex(null)}
          onIndexChange={setViewerIndex}
        />
      )}

      {/* Auth Modal */}
      {showAuth && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 animate-scale-in relative">
            <button
              type="button"
              onClick={() => setShowAuth(false)}
              className="absolute top-4 right-4 text-[#1F2933]/50 hover:text-[#1F2933]"
            >
              ✕
            </button>
            
            <h2 className="font-manrope font-bold text-xl text-[#1F2933] mb-2">
              {authMode === "login" ? "Welcome back!" : "Create an account"}
            </h2>
            <p className="text-sm text-[#1F2933]/60 mb-6">
              {authMode === "login" 
                ? "Sign in to submit your request" 
                : "Create an account to track your requests"}
            </p>
            
            {/* Google Sign-in */}
            <Button 
              type="button"
              variant="outline"
              className="w-full mb-4 py-5 rounded-xl border-gray-200"
              onClick={googleLogin}
              data-testid="google-auth-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
            
            {/* Divider */}
            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-[#1F2933]/50">or continue with email</span>
              </div>
            </div>
            
            <form onSubmit={handleAuth} className="space-y-4">
              {authMode === "register" && (
                <div>
                  <Label htmlFor="auth-name">Name</Label>
                  <Input
                    id="auth-name"
                    name="name"
                    value={authData.name}
                    onChange={handleAuthChange}
                    className="mt-1"
                    required
                  />
                </div>
              )}
              
              <div>
                <Label htmlFor="auth-email">Email</Label>
                <Input
                  id="auth-email"
                  name="email"
                  type="email"
                  value={authData.email}
                  onChange={handleAuthChange}
                  className="mt-1"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="auth-password">Password</Label>
                <Input
                  id="auth-password"
                  name="password"
                  type="password"
                  value={authData.password}
                  onChange={handleAuthChange}
                  className="mt-1"
                  required
                  minLength={6}
                />
              </div>
              
              <Button 
                type="submit"
                className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white"
              >
                {authMode === "login" ? "Sign In" : "Create Account"}
              </Button>
            </form>
            
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setAuthMode(authMode === "login" ? "register" : "login")}
                className="text-sm text-[#0FA3B1] hover:underline"
              >
                {authMode === "login" 
                  ? "Don't have an account? Sign up" 
                  : "Already have an account? Sign in"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
